import { useQueries, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { formatDate, formatRelative } from '../ui/format';
import { Badge, EmptyState, Loading, PageHeader, Panel } from '../ui/Page';
import { searchEntity } from '../crm/api';
import { searchInbox } from '../inbox/api';
import { searchChat } from '../chat/api';
import { listTickets } from '../support/api';
import { useMembers } from '../ui/UserSelect';

/**
 * Global search.
 *
 * A request per thing rather than one, because there is no cross-entity search endpoint and
 * inventing one on the client — merging and re-ranking several cursors — would produce a list
 * whose order nobody could explain. A panel each, only the ones with hits, is honest and is what
 * somebody scanning for a name actually reads.
 *
 * It reaches records, tickets, and what people said: the customer conversations somebody may see
 * and the internal rooms they belong to. Each of those three is scoped by the server — the search
 * box must never be a way to reach further than browsing would.
 */

const ENTITIES = [
  { key: 'leads', path: '/crm/leads', labelKey: 'nav.leads' },
  { key: 'contacts', path: '/crm/contatos', labelKey: 'nav.contacts' },
  { key: 'companies', path: '/crm/empresas', labelKey: 'nav.companies' },
  { key: 'opportunities', path: '/crm/oportunidades', labelKey: 'nav.opportunities' },
] as const;

interface Row {
  id: string;
  name?: string;
  title?: string;
  first_name?: string;
  last_name?: string;
  email?: string | null;
  created_at: string;
}

export function SearchPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const query = params.get('q')?.trim() ?? '';

  const results = useQueries({
    queries: ENTITIES.map((entity) => ({
      queryKey: ['search', entity.key, query],
      queryFn: () =>
        searchEntity<Row>(entity.key, {
          filter: {
            combinator: 'AND' as const,
            conditions: [{ field: nameField(entity.key), operator: 'contains', value: query }],
          },
          sort: { field: 'created_at', direction: 'desc' as const },
          limit: 10,
        }),
      enabled: query.length > 0,
      retry: false,
    })),
  });

  /*
   * Conversations, chat and tickets, each from the endpoint that owns them.
   *
   * `enabled` on a two-character minimum matches what the two message endpoints accept: firing a
   * request the server will refuse would paint an error where somebody is still typing.
   */
  const inbox = useQuery({
    queryKey: ['search-inbox', query],
    queryFn: () => searchInbox(query, 10),
    enabled: query.length >= 2,
    retry: false,
  });

  const chat = useQuery({
    queryKey: ['search-chat', query],
    queryFn: () => searchChat(query, 10),
    enabled: query.length >= 2,
    retry: false,
  });

  const tickets = useQuery({
    queryKey: ['search-tickets', query],
    queryFn: () => listTickets({ search: query, limit: 10 }),
    enabled: query.length >= 2,
    retry: false,
  });

  const { options: people } = useMembers();

  if (!query) {
    return (
      <div className="view">
        <PageHeader title={t('shell.search')} />
        <EmptyState title={t('search.typeSomething')} description={t('search.typeSomethingBody')} />
      </div>
    );
  }

  const conversations = inbox.data?.messages ?? [];
  const chatHits = chat.data?.messages ?? [];
  const ticketHits = tickets.data?.tickets ?? [];

  const total =
    results.reduce((sum, result) => sum + (result.data?.results.length ?? 0), 0) +
    conversations.length +
    chatHits.length +
    ticketHits.length;

  const settled =
    results.every((result) => !result.isLoading) &&
    !inbox.isLoading &&
    !chat.isLoading &&
    !tickets.isLoading;

  return (
    <div className="view">
      <PageHeader
        title={t('search.resultsFor').replace('{query}', query)}
        description={settled ? t('crm.results').replace('{count}', String(total)) : undefined}
      />

      {settled && total === 0 && (
        <EmptyState title={t('search.nothing')} description={t('search.nothingBody')} />
      )}

      {ENTITIES.map((entity, index) => {
        const result = results[index]!;
        const rows = result.data?.results ?? [];

        // A section with nothing in it is noise; only the ones with hits are shown.
        if (result.isLoading) return <Loading key={entity.key} />;
        if (rows.length === 0) return null;

        return (
          <Panel key={entity.key} title={t(entity.labelKey)}>
            <ul className="home-list">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link to={`/crm/registro/${entity.key}/${row.id}`}>
                    <span className="cell-stack">
                      <span className="strong">{labelOf(row)}</span>
                      {row.email && <span className="muted small">{row.email}</span>}
                    </span>
                    <span className="muted small">{formatDate(row.created_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        );
      })}

      {ticketHits.length > 0 && (
        <Panel title={t('nav.tickets')}>
          <ul className="home-list">
            {ticketHits.map((ticket) => (
              <li key={ticket.id}>
                <Link to={`/atendimento/chamados/${ticket.id}`}>
                  <span className="cell-stack">
                    <span className="strong">{`#${ticket.number} · ${ticket.subject}`}</span>
                    <span className="muted small">{t(`support.status.${ticket.status}`)}</span>
                  </span>
                  <span className="muted small">{formatRelative(ticket.createdAt)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {conversations.length > 0 && (
        <Panel title={t('search.inConversations')}>
          <ul className="home-list">
            {conversations.map((message) => (
              <li key={message.id}>
                <Link to={`/atendimento/caixa?conversation=${message.conversationId}`}>
                  <span className="cell-stack">
                    <span className="cell-inline">
                      <span className="strong">{message.peerName ?? message.peerAddress}</span>
                      <Badge tone="neutral">{t(`channels.kind.${message.channelKind}`)}</Badge>
                    </span>
                    <span className="muted small">{excerpt(message.body, query)}</span>
                  </span>
                  <span className="muted small">{formatRelative(message.createdAt)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {chatHits.length > 0 && (
        <Panel title={t('search.inChat')}>
          <ul className="home-list">
            {chatHits.map((message) => (
              <li key={message.id}>
                <Link to={`/chat?conversation=${message.conversation_id}`}>
                  <span className="cell-stack">
                    <span className="strong">
                      {people.find((person) => person.id === message.author_id)?.name ??
                        t('work.systemAuthor')}
                    </span>
                    <span className="muted small">{excerpt(message.body, query)}</span>
                  </span>
                  <span className="muted small">{formatRelative(message.created_at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

/**
 * The words around the match, rather than the first line of the message.
 *
 * A search result showing the opening of a long message is a result somebody has to open to know
 * whether it is the one. Falls back to the start when the term is not a literal substring, which
 * happens often: the server matches stems, so "fatura" finds "faturas".
 */
export function excerpt(body: string, query: string, radius = 60): string {
  const at = body.toLowerCase().indexOf(query.toLowerCase().split(/\s+/)[0] ?? '');
  const start = at < 0 ? 0 : Math.max(0, at - radius);
  const end = Math.min(body.length, start + radius * 2);
  const slice = body.slice(start, end).trim();

  return `${start > 0 ? '…' : ''}${slice}${end < body.length ? '…' : ''}`;
}

/** Opportunities are titled, everything else is named. */
function nameField(entity: string): string {
  return entity === 'opportunities' ? 'title' : 'name';
}

function labelOf(row: Row): string {
  if (row.name) return row.name;
  if (row.title) return row.title;
  if (row.first_name) return [row.first_name, row.last_name].filter(Boolean).join(' ');

  return row.id.slice(0, 8);
}
